import React, { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { usePulseBackground } from "../pulse-background";
import "./nav.scss";

interface NavItem {
  label: string;
  route?: string;
  onClick?: () => void;
  isToggle?: boolean;
  toggled?: boolean;
}

interface NavButtonFloatingProps {
  label?: string;
  items?: NavItem[];
  children?: React.ReactNode;
}

export const NavButtonFloating: React.FC<NavButtonFloatingProps> = ({
  label = "Menu",
  items = [],
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [waveTriggered, setWaveTriggered] = useState(false);
  const [waveResetting, setWaveResetting] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const navTriggerRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevOpenRef = useRef(false);
  const { spawn } = usePulseBackground();

  const requestClose = () => {
    if (!isOpen || isClosing) return;
    setIsClosing(true);
  };

  const handleCloseAnimationEnd = (
    event: React.TransitionEvent<HTMLDivElement>,
  ) => {
    if (
      !isClosing ||
      event.target !== event.currentTarget ||
      event.propertyName !== "width"
    )
      return;
    setIsOpen(false);
    setIsClosing(false);
  };

  const renderItem = (item: NavItem, index: number) => {
    if (item.route) {
      return (
        <NavLink
          key={index}
          to={item.route}
          className={({ isActive }) =>
            `nav-link${isActive ? " nav-link--active" : ""}`
          }
          onClick={requestClose}
        >
          {item.label}
        </NavLink>
      );
    }

    return (
      <button
        key={index}
        type="button"
        className={`nav-action ${item.isToggle ? "nav-toggle" : ""}`}
        onClick={() => {
          requestClose();
          item.onClick?.();
        }}
      >
        {item.label}
      </button>
    );
  };

  useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      /* Disable for now on menu open
      spawn({
        placement: "anchor",
        anchorRef: containerRef,
        mode: "once",
        removeOnComplete: true,
        zIndex: "calc(var(--z-top) - 1)",
        photoSelectionType: "nav",
      });
      */
    }
    prevOpenRef.current = isOpen;
  }, [isOpen, spawn]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!isOpen) return;
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        requestClose();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isOpen, isClosing]);

  useEffect(() => {
    if (isOpen) return;

    const handleMouseMove = (event: MouseEvent) => {
      const isInRightThird = event.clientX > (window.innerWidth * 2) / 3;

      if (
        isInRightThird &&
        !waveTriggered &&
        !waveResetting &&
        !hasUserInteracted
      ) {
        setWaveResetting(false);
        setWaveTriggered(true);
      } else if (!isInRightThird && waveTriggered) {
        setWaveTriggered(false);
      }
    };

    const handleMouseLeave = () => {
      setWaveTriggered(false);
      setWaveResetting(true);
      setTimeout(() => setWaveResetting(false), 0);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [isOpen, waveTriggered, waveResetting, hasUserInteracted]);

  return (
    <>
      <div
        className={`nav-wave ${waveTriggered ? "nav-wave--active" : ""} ${waveResetting ? "nav-wave--resetting" : ""}`}
      />

      <div
        className={`nav-floating ${isOpen ? "nav-floating--open" : ""} ${isClosing ? "nav-floating--closing" : ""}`}
        ref={containerRef}
        onTransitionEnd={handleCloseAnimationEnd}
      >
        <div className="nav-panel">
          <div className="nav-panel__content">
            <div className="nav-panel__inner">
              {children && (
                <div className="nav-floating__children" onClick={requestClose}>
                  {children}
                </div>
              )}
              <ul className="nav-list">{items.map(renderItem)}</ul>
            </div>
          </div>
        </div>

        <button
          ref={navTriggerRef}
          type="button"
          className={`nav-trigger ${isOpen && !isClosing ? "nav-trigger--open" : ""}`}
          onClick={() => {
            setHasUserInteracted(true);
            if (isClosing) return;
            if (isOpen) requestClose();
            else setIsOpen(true);
          }}
          aria-label={isOpen && !isClosing ? "Close menu" : "Open menu"}
        >
          <span className="nav-trigger__icon" />
        </button>
      </div>
    </>
  );
};
